import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RegisterFormSchema, RegisterFormData } from '../../lib/types/auth';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import { useOrbs } from '../../context/OrbContext';

interface FormErrors extends RegisterFormData {
  global?: string;
}

/**
 * Default to the bundled EARRINGSLOGIN.png so the desktop register page
 * always has its hero image on the right, even if no split-screen config has
 * been saved in Firestore yet. Any image configured in the settings page will
 * still take precedence.
 */
const DEFAULT_LOGIN_IMAGE = '/EARRINGSLOGIN.png';

export const Register: React.FC = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { orbColors } = useOrbs();

  // Theme + split-screen image config (mirrors Login.tsx).
  const [splitScreenConfig, setSplitScreenConfig] = useState({
    enabled: false,
    images: [] as string[],
    overlayColor: '#000000',
    overlayOpacity: 0.3,
    imagePosition: 'center'
  });
  const [splitScreenLoaded, setSplitScreenLoaded] = useState(false);

  useEffect(() => {
    const loadSplitScreenConfig = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'settings', 'split_screen_config'));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSplitScreenConfig({
            enabled: data.enabled === true,
            images: Array.isArray(data.images) ? data.images : [],
            overlayColor: data.overlayColor || '#000000',
            overlayOpacity: typeof data.overlayOpacity === 'number' ? data.overlayOpacity : 0.3,
            imagePosition: data.imagePosition || 'center'
          });
        }
        setSplitScreenLoaded(true);
      } catch (error) {
        console.error('🖼️ Register: Error loading split screen config:', error);
        setSplitScreenLoaded(true);
      }
    };
    loadSplitScreenConfig();
  }, []);

  // Listen for theme changes (kept for parity with Login; the new layout is
  // light-only for now).
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'login_blob_colors'), () => {});
    return () => unsubscribe();
  }, []);

  const randomSplitScreenImage = useMemo(() => {
    const images = splitScreenConfig.images.length > 0
      ? splitScreenConfig.images
      : orbColors.splitScreenImages;
    if (images && images.length > 0) {
      const randomIndex = Math.floor(Math.random() * images.length);
      return images[randomIndex];
    }
    return orbColors.splitScreenImageUrl || DEFAULT_LOGIN_IMAGE;
  }, [splitScreenConfig.images, orbColors.splitScreenImages, orbColors.splitScreenImageUrl]);

  // Single layout — no toggling. Mobile shows form-only via Tailwind's
  // `hidden lg:block` on the image pane; desktop shows form + image.
  void splitScreenLoaded;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<FormErrors>({
    resolver: zodResolver(RegisterFormSchema)
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setLoading(true);
      setGlobalError(null);
      await registerUser(data.email, data.password, data.displayName);

      setRegistrationSuccess(true);
      toast.success('Registration successful! Your account is pending approval.');

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to register. Please try again.';
      setGlobalError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Right pane: image card (shared between form view and success view).
  // Hidden on mobile, shown on lg+ as the right column of the split layout.
  const imagePane = (
    <div className="relative overflow-hidden hidden lg:block">
      <img
        src={randomSplitScreenImage}
        alt="Welcome to the Store Center"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          objectPosition: splitScreenConfig.imagePosition || orbColors.splitScreenImagePosition || 'center'
        }}
      />
      <div className="absolute inset-x-0 bottom-0 pt-32 pb-12 px-12 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
        <p className="text-6xl font-light text-white leading-[1.05] tracking-tight drop-shadow-lg">
          Welcome to the<br />
          <span className="font-semibold">Store Center</span>
        </p>
      </div>
    </div>
  );

  // ─── Success View ───────────────────────────────────────────────────────
  if (registrationSuccess) {
    const successPane = (
      <div className="flex items-center justify-center px-8 sm:px-12 lg:px-20 py-12 bg-white">
        <div className="w-full max-w-sm text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4 }}
            className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-6"
          >
            <CheckCircle className="h-8 w-8 text-green-600" />
          </motion.div>
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Account Created</h1>
          <p className="text-sm text-gray-500 mt-3 leading-relaxed">
            Your account has been created and is pending approval from an administrator.
            You'll receive an email notification once your account is approved.
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Redirecting to the login page in 3 seconds…
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full py-3 mt-6 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );

    return (
      <div className="relative min-h-screen p-4 lg:p-6 bg-black">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-2rem)] lg:min-h-[calc(100vh-3rem)] bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] overflow-hidden">
          {successPane}
          {imagePane}
        </div>
      </div>
    );
  }

  // ─── Form View ──────────────────────────────────────────────────────────
  const formPane = (
    <div className="flex items-center justify-center px-8 sm:px-12 lg:px-20 py-10 bg-white">
      <div className="w-full max-w-sm">
        <img
          src="/cco-black.png"
          alt="Chorost & Co."
          className="mx-auto w-72 mb-8 opacity-90"
        />
        <div className="text-center mb-7">
          <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">Create Account</h1>
          <p className="text-sm text-gray-500 mt-2">Join the Store Center</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">First and Last Name</label>
            <input
              type="text"
              placeholder="Jane Smith"
              autoComplete="name"
              className="w-full px-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-100 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 transition-all"
              {...register('displayName')}
            />
            {errors.displayName?.message && (
              <p className="mt-1.5 text-xs text-red-500">{errors.displayName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Company Email</label>
            <input
              type="email"
              placeholder="your.name@company.com"
              autoComplete="email"
              className="w-full px-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-100 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 transition-all"
              {...register('email')}
            />
            {errors.email?.message && (
              <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
            <input
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              className="w-full px-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-100 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 transition-all"
              {...register('password')}
            />
            {errors.password?.message && (
              <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Confirm Password</label>
            <input
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              className="w-full px-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-100 focus:border-gray-300 focus:ring-1 focus:ring-gray-300 transition-all"
              {...register('confirmPassword', {
                validate: value => value === watch('password') || 'Passwords do not match'
              })}
            />
            {errors.confirmPassword?.message && (
              <p className="mt-1.5 text-xs text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>

          <p className="text-[11px] text-gray-500 leading-relaxed pt-1">
            <span className="font-semibold text-gray-700">Note:</span> Your account will be pending approval after registration.
          </p>

          <motion.button
            type="submit"
            disabled={loading}
            className="w-full py-3 mt-1 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            {loading ? (
              <motion.div
                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mx-auto"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : 'Create Account'}
          </motion.button>

          <p className="text-center text-xs text-gray-500 pt-2">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-gray-900 hover:underline">
              Sign In
            </Link>
          </p>

          {globalError && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="p-3 rounded-xl bg-red-50 border border-red-200"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800 leading-relaxed">{globalError}</p>
              </div>
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen p-4 lg:p-6 bg-black">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-2rem)] lg:min-h-[calc(100vh-3rem)] bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] overflow-hidden">
        {formPane}
        {imagePane}
      </div>
    </div>
  );
};
